import { graphql, useFragment } from "react-relay";
import type { postFragment$key } from "./__generated__/postFragment.graphql";
import { Card } from "@/components/ui/card";
import { PostBottomActions } from "./post-bottom-actions";
import { PostHeader } from "./post-header";
import { PostContent } from "./post-content";
import { PostCover } from "./post-cover";
import { PostComments } from "./post-comments";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import type { CommentOrdering } from "./__generated__/postCommentsQuery.graphql";
import { PostAttachmentsPreview } from "./post-attachments-preview";

export function Post({
	post,
	onUpdate,
}: {
	post: postFragment$key;
	onUpdate?: () => void;
}) {
	const { comment_order: commentOrder } = useSearch({
		from: "/_visibility/_topnav/comunidade/s/$space/posts/$post",
	});
	const navigate = useNavigate({ from: "/comunidade/s/$space/posts/$post" });
	const data = useFragment<postFragment$key>(
		graphql`
      fragment postFragment on Post @argumentDefinitions(club: { type: "String!" }) {
        title
        slug
        space {
          slug
        }
        ...postBottomActionsFragment
        ...postHeaderFragment @arguments(club: $club)
        ...postContentFragment
        ...postCoverFragment
        ...postComments_post
        ...postAttachmentsPreview_post
      }
    `,
		post,
	);

	const orderBy: CommentOrdering = useMemo(() => {
		switch (commentOrder) {
			case "newest":
				return "NEWEST";
			case "oldest":
				return "OLDEST";
			default:
				return "TRENDING";
		}
	}, [commentOrder]);

	const commentOrderingToParam = (val: CommentOrdering) => {
		switch (val) {
			case "NEWEST":
				return "newest";
			case "OLDEST":
				return "oldest";
			default:
				return "trending";
		}
	};

	if (!data) {
		return null;
	}

	return (
		<>
			<Card className="prose pt-0 flex flex-col overflow-hidden gap-0 dark:prose-invert w-full min-w-full max-md:rounded-none max-md:shadow-none max-md:border-none">
				<PostCover post={data} />

				<div className="px-6 pt-6 max-md:px-3 max-md:pb-3 flex flex-col gap-6">
					<PostHeader post={data} onUpdate={onUpdate} />

					<h1 className="font-semibold text-3xl/6 sm:text-2xl/6 h-fit pb-0 mb-0">
						{data.title}
					</h1>

					<PostContent post={data} />

					<PostAttachmentsPreview post={data} />

					<PostBottomActions post={data} />
				</div>
			</Card>

			<PostComments
				post={data}
				orderBy={orderBy}
				setOrderBy={(val: CommentOrdering) =>
					navigate({
						search: (s) => ({
							...s,
							comment_order: commentOrderingToParam(val),
						}),
					})
				}
			/>
		</>
	);
}
